import TranslateManager from 'ts-translate-js';
import { some } from 'lodash';
import { SeenCreditCardCondition } from './models';
import { WarningKeyEnum, Warning, TicketSelectionWarningReport } from './models/ticketselectionwarning';
const tr = TranslateManager.boundTR;
const trN = tr;



export function getAddToCartErrorMessages(code) {
    if (code === "ERR_INVALID_OPERATION") return tr("general.invalidoperation");
    else if (code === "ERR_PRICE_NOT_IN_SALE_DEFAULT") return tr("membership.addcarderror.price.default");
    else if (code === "ERR_PRICE_SALE_NOT_STARTED_YET") return tr("membership.addcarderror.price.salenotstartyet");
    else if (code === "ERR_PRICE_SALE_ENDED") return tr("membership.addcarderror.price.saleended");
    else if (code === "ERR_PRICE_SOLD_OUT") return tr("membership.addcarderror.price.soldout");
    else if (code === "ERR_EVENT_NOT_IN_SALE") return tr("membership.addcarderror.card.notinsale");
    else if (code === "ERR_EVENT_SOLD_OUT") return tr("membership.addcarderror.card.soldout");
    else if (code === "ERR_NO_TICKET_FOUND") return tr("membership.addcarderror.card.ticketnotfound");
    else if (code === "ERR_TOO_MANY_TICKETS_IN_TRANSACTION") return tr("membership.addcarderror.card.toomanytickets");
    else return null;
}

export function analyseAddToCartErrorMessage(result) {
    let errorMessage = null;
    if (result.ErrorCode === "ERR_PRICE_NOT_IN_SALE") // This message has sub messages
        errorMessage = getAddToCartErrorMessages(result.ErrorSubCode) != null ? getAddToCartErrorMessages(result.ErrorSubCode) : getAddToCartErrorMessages("ERR_PRICE_NOT_IN_SALE_DEFAULT");
    else
        errorMessage = getAddToCartErrorMessages(result.ErrorCode); // Else, all other messages

    if (errorMessage === null) { // If no message found, let's show a generic error
        if (!result.CommError) // Should this go at first
            errorMessage = result.Message;
        else
            errorMessage = tr("general.communicationerroroccuredpleaseretry");
    }
    return errorMessage;
}







/**
 * Get price selection warnings with specific filtering for the price
 * @param warnings The initial warning list
 * @return The warning list with specific filtering
 */
export function getWarningsListWithSpecificFilters(warnings: Warning[]): Warning[] {
    const hasLimitReachedWarning = some(warnings, ['key', 'limit_reached']);
    if (hasLimitReachedWarning) {
        return warnings.filter(w => w.key === "limit_reached");
    }
    return warnings;
}

/**


/**
 * Checks if a credit card condition has already been seen, to determine if the modal needs to be shown to the client.
 * @param creditCardConditionId The credit card contion ID to verify
 * @param activeCreditCardConditions The credit card conditions that are in the cart
 * @param alreadySeenCreditCardConditions The credit card conditions that are not in the cart but have already been seen along selection process
 * @returns `true` if the credit card condition has been seen ; `false` otherwise.
 */
export function isPriceGroupCreditCardConditionSeen(creditCardConditionId: string, activeCreditCardConditions: any[], alreadySeenCreditCardConditions: SeenCreditCardCondition): boolean {
    const activeCreditCardConditionIds = activeCreditCardConditions.map((cc) => cc.id);
    return activeCreditCardConditionIds.includes(creditCardConditionId) || alreadySeenCreditCardConditions[creditCardConditionId];
}


/**
 * Get the expired tickets in the warning report
 * @param warningReport The warning report
 * @returns The list of expired ticket IDs
 */
export function getExpiredTicketsInWarningReport(warningReport: TicketSelectionWarningReport, isAPackageSale: boolean): { expiredTicketIds:number[], warningMessage: string } {
    let report = { expiredTicketIds:[], warningMessage:"" };

    if(warningReport && warningReport.generalWarnings) {
        // Find all the expired ticket warnings
        const expTicketWarnings = warningReport.generalWarnings.filter(w => 
            w.key === WarningKeyEnum.HAVE_EXPIRED_TICKETS 
            || w.key === WarningKeyEnum.HAVE_ALREADY_TICKETS_IN_CART);

        report = { 
            expiredTicketIds: expTicketWarnings.map(w => w.expiredTicketIds).flat(), // Gather all expired ticket IDs
            warningMessage: expTicketWarnings.length > 0 ? getWarningMessage(expTicketWarnings[0], isAPackageSale) : "" // Return the first error message
        };
    }
    
    return report;
}

/**
 * Get the warning message for a specific warning
 * @param waring The warning to get the message
 * @returns return the message of the warning
 */
export function getWarningMessage(warning: Warning, isAPackageSale: boolean): string {

    switch(warning.key) {
        case WarningKeyEnum.QTY_NEEDED:
            if(isAPackageSale)
                return trN("Nécessite 1 forfait pour profiter de ce tarif", "Nécessite {n} forfaits pour profiter de ce tarif", { n: warning.qtyNeeded });
            else
                return trN("Nécessite 1 billet pour profiter de ce tarif", "Nécessite {n} billets pour profiter de ce tarif", { n: warning.qtyNeeded });

        case WarningKeyEnum.MISSING_QTY:
            if(isAPackageSale)
                return trN("Manque 1 forfait pour profiter de ce tarif", "Manque {n} forfaits pour profiter de ce tarif", { n: warning.missingQty });
            else
                return trN("Manque 1 billet pour profiter de ce tarif", "Manque {n} billets pour profiter de ce tarif", { n: warning.missingQty });

        case WarningKeyEnum.MISSING_QTY_ACCESS_SALE:
            if(isAPackageSale)
                return trN("Manque 1 forfait pour profiter de cette vente", "Manque {n} forfaits pour profiter de cette vente", { n: warning.missingQty });
            else
                return trN("Manque 1 billet pour profiter de ce tarif", "Manque {n} billets pour profiter de ce tarif", { n: warning.missingQty });
        case WarningKeyEnum.LIMIT_REACHED:
            return tr("Limite atteinte!");
        case WarningKeyEnum.PROMO_LIMIT_REACHED:
            return tr("Limite atteinte pour la promo!");
        case WarningKeyEnum.QTY_TO_REMOVE:
            if(isAPackageSale)
                return trN("Enlever 1 forfait pour profiter de ce tarif", "Enlever {n} forfaits pour profiter de ce tarif", { n: warning.qtyToRemove });
            else
                return trN("Enlever 1 billet pour profiter de ce tarif", "Enlever {n} billets pour profiter de ce tarif", { n: warning.qtyToRemove });
        
        case WarningKeyEnum.REQUIRED_CREDIT_CARD:
            return tr("Nécessite une carte de crédit {creditCardName}", {creditCardName: warning.creditCardName});

        case WarningKeyEnum.REQUIRED_MEMBERSHIP_CARD:
            return tr("Nécessite une carte {cardName}", {cardName: warning.cardName});
            
        case WarningKeyEnum.MULTIPLE_OF_NEEDED:
            if(isAPackageSale)
                return trN("Nécessite un multiple de 1 forfait pour profiter de ce tarif", "Nécessite un multiple de {n} forfaits pour profiter de ce tarif", { n: warning.multipleOfNeeded });
            else
                return trN("Nécessite un multiple de 1 billet pour profiter de ce tarif", "Nécessite un multiple de {n} billets pour profiter de ce tarif", { n: warning.multipleOfNeeded });

        case WarningKeyEnum.MULTIPLE_OF_NEEDED_ACCESS_SALE:
            if(isAPackageSale)
                return trN("Nécessite un multiple de 1 forfait pour profiter de cette vente", "Nécessite un multiple de {n} forfaits pour profiter de cette vente", { n: warning.multipleOfNeeded });
            else
                return trN("Nécessite un multiple de 1 billet pour profiter de cette vente", "Nécessite un multiple de {n} billets pour profiter de cette vente", { n: warning.multipleOfNeeded });

        case WarningKeyEnum.MAXIMUM_QTY:
            if(isAPackageSale)
                return trN("Maximum de 1 forfait par commande", "Maximum de {n} forfaits par commande", { n: warning.maximumQty });
            else
                return trN("Maximum de 1 billet par commande", "Maximum de {n} billets par commande", { n: warning.maximumQty });

        case WarningKeyEnum.HAVE_EXPIRED_TICKETS:
            if(isAPackageSale)
                return tr("Désolé, certains forfaits de votre sélection ne sont plus disponibles. Veuillez en choisir de nouveau.");
            else
                return tr("Désolé, certains billets de votre sélection ne sont plus disponibles. Veuillez en choisir de nouveau.");

        case WarningKeyEnum.HAVE_ALREADY_TICKETS_IN_CART:
            if(isAPackageSale)
                return tr("Certains forfaits de votre sélection sont déjà dans votre panier d'achats.");
            else
                return tr("Certains billets de votre sélection sont déjà dans votre panier d'achats.");

        case WarningKeyEnum.SELECTION_LEAVES_HOLES_IN_VENUE:
            return tr("Votre sélection laisse un ou plusieurs siège(s) vide(s) isolé(s) dans la salle. Veuillez modifier votre sélection.");
            
        default:
            return tr("Une erreur est survenue ({key})", {key: warning.key});
    }
}